import React, { useState } from 'react';
import { usePartner } from '@contexts/PartnerContext';
import useGetCarehomeInfo from '@hooks/useGetCarehomeInfo';
import { CarehomeAddressType } from '@typings/CarehomeInfo';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { addCarehomeAddress, editCarehomeAddress } from '@api/carehome-info';
import { SubmitStatus, SubmitStatusEnum } from '@typings/common';
import CarehomeAddress from './components/CarehomeAddress';
import PhoneNumber from './components/PhoneNumber';

export enum AddressPageType {
  ADD = 'Add',
  EDIT = 'Edit',
}
const OrganizationDetails: React.FC = () => {
  const { selectedPartnerId } = usePartner();

  const [carehomeAddress, setCarehomeAddress] = useState<CarehomeAddressType>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postCode: '',
  });
  const [addAddressStatus, setAddAddressStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const {
    savedCarehomeAddressFromTeams,
    phoneNumber,
    setPhoneNumber,
    addressPageType,
  } = useGetCarehomeInfo(selectedPartnerId);

  const handleAddCarehomeInfo = async () => {
    if (selectedPartnerId) {
      setAddAddressStatus(prev => ({ ...prev, loading: true }));
      try {
        const response = await addCarehomeAddress(
          selectedPartnerId,
          carehomeAddress
        );

        if (response && response.status === 'success') {
          setAddAddressStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message: 'The address has been saved',
          }));

          setTimeout(() => {
            setAddAddressStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setAddAddressStatus(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const handleEditCarehomeInfo = async () => {
    if (selectedPartnerId) {
      setAddAddressStatus(prev => ({ ...prev, loading: true }));
      try {
        const response = await editCarehomeAddress(
          selectedPartnerId,
          carehomeAddress
        );
        if (response && response.status === 'success') {
          setAddAddressStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message: 'The address has been saved',
          }));

          setTimeout(() => {
            setAddAddressStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setAddAddressStatus(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const onSaveAddress = (e: any) => {
    e.preventDefault();

    if (addressPageType === AddressPageType.ADD) {
      handleAddCarehomeInfo();
    } else {
      handleEditCarehomeInfo();
    }
  };

  return (
    <div className="organization-details mt-5">
      <h1 className="govuk-heading-l">Teams</h1>
      <h2 className="govuk-heading-m">Your organisation&apos;s details</h2>
      <p className="govuk-body">
        You are required to update your organisations contact details and keep
        them up to date. These details will be included in printed, published
        and shared Advance Care Plans.
      </p>
      <fieldset className="govuk-fieldset">
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
          <h2 className="govuk-fieldset__heading">
            Your organisation&apos;s address
          </h2>
        </legend>
        {addAddressStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            title={addAddressStatus.message}
            duration={3000}
          />
        )}
        <CarehomeAddress
          carehomeAddress={carehomeAddress}
          setCarehomeAddress={setCarehomeAddress}
          savedCarehomeAddressFromTeams={savedCarehomeAddressFromTeams}
        />
        <button
          type="button"
          className="govuk-button"
          onClick={onSaveAddress}
          disabled={addAddressStatus.loading}
        >
          Save
        </button>
      </fieldset>

      <PhoneNumber phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber} />
    </div>
  );
};

export default OrganizationDetails;
